// Shared, pure presentation helpers for the release surfaces (hub + command center). No hooks, no I/O — pure
// functions so they unit-test directly and both the hub card and the detail hero render identically. Money is
// integer cents; dates are zoneless wall-clock DOMAIN dates parsed as local (suffix T12:00:00
// so the device timezone can never shift the calendar day). The release-type/status vocabularies mirror the
// server CHECK sets (no client enum in the generated schema — `IReleasesService`/`Release.cs`), presentation
// only (the values are policy, the labels are UI).

/** The release TYPE vocabulary (single | ep | album) — server CHECK set, presentation labels. */
export const RELEASE_TYPES = ["single", "ep", "album"] as const;
export type ReleaseType = (typeof RELEASE_TYPES)[number];

const TYPE_LABELS: Record<string, string> = { single: "Single", ep: "EP", album: "Album" };

/** The human label for a release type ("single" → "Single"); unknown values title-case as a fallback. */
export function releaseTypeLabel(type: string | null | undefined): string {
  if (!type) return "Release";
  return TYPE_LABELS[type] ?? type.toUpperCase();
}

/** The release LIFECYCLE vocabulary (draft | in_cycle | released | cancelled) — server CHECK set. */
export const RELEASE_STATUSES = ["draft", "in_cycle", "released", "cancelled"] as const;
export type ReleaseStatus = (typeof RELEASE_STATUSES)[number];

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  in_cycle: "In cycle",
  released: "Released",
  cancelled: "Cancelled",
};

/** The human label for a release status ("in_cycle" → "In cycle"). */
export function releaseStatusLabel(status: string | null | undefined): string {
  if (!status) return "Draft";
  return STATUS_LABELS[status] ?? status.replace(/_/g, " ");
}

/**
 * The BUILDER hero eyebrow's lifecycle phrase — `In cycle · pre-release`, `Released`, `Draft`, `Cancelled`.
 *
 * Only the in-cycle case differs from {@link releaseStatusLabel}, and the extra word is the reference's
 * (`'In cycle · pre-release'`): on the page where a band is assembling the rollout, "in cycle" alone reads as
 * a state and "pre-release" says the record is not out yet — the distinction the whole builder is about.
 *
 * The reference is BINARY here (released vs everything else), so it would call a dateless draft "in cycle ·
 * pre-release". Ours keeps the four-value vocabulary, because we persist `draft` and `cancelled` as authoring
 * states a date cannot express — see {@link displayStatus}.
 */
export function releaseStatusPhrase(status: string | null | undefined): string {
  return status === "in_cycle" ? "In cycle · pre-release" : releaseStatusLabel(status);
}

/**
 * The STATUS PILL tone key (drives `[data-tone]` in the SCSS): gray DRAFT · crimson-accent IN CYCLE · green
 * RELEASED · muted CANCELLED. On-token only (no literal colors — the tokens:audit firewall).
 */
export function releaseStatusTone(status: string | null | undefined): string {
  switch (status) {
    case "in_cycle":
      return "in_cycle";
    case "released":
      return "released";
    case "cancelled":
      return "cancelled";
    default:
      return "draft";
  }
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Parse a YYYY-MM-DD wall-clock domain date to a LOCAL Date at noon (DST-proof). null on blank/bad.*/
export function parseDomainDate(date: string | null | undefined): Date | null {
  if (!date) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!m) return null;
  const [, y, mo, d] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d), 12, 0, 0);
}

/** Format a domain date as "Fri, May 8, 2026" (the hub spotlight / hero line). Blank in → "". */
export function formatReleaseDate(date: string | null | undefined): string {
  const d = parseDomainDate(date);
  if (!d) return "";
  return `${WEEKDAYS[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** Format a domain date as "May 8, 2026" (no weekday) — KPI tiles / timeline rows. Blank in → "". */
export function formatShortDate(date: string | null | undefined): string {
  const d = parseDomainDate(date);
  if (!d) return "";
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/**
 * Whole days from `today` (local midnight) to the release date. Positive = days OUT (upcoming), 0 = release
 * day, negative = days since (out). null when there is no date. Wall-clock math — the device TZ never shifts it.
 */
export function daysToRelease(releaseDate: string | null | undefined, today = new Date()): number | null {
  const d = parseDomainDate(releaseDate);
  if (!d) return null;
  const dueMidnight = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((dueMidnight.getTime() - todayMidnight.getTime()) / 86_400_000);
}

/** The countdown label: "37 days out" / "Out today" / "Out 12 days ago" / "" (no date). */
export function countdownLabel(releaseDate: string | null | undefined, today = new Date()): string {
  const n = daysToRelease(releaseDate, today);
  if (n === null) return "";
  if (n === 0) return "Out today";
  if (n > 0) return `${n} day${n === 1 ? "" : "s"} out`;
  const ago = -n;
  return `Out ${ago} day${ago === 1 ? "" : "s"} ago`;
}

/**
 * The featured banner's meta line — `1 track · Fri, Sep 18, 2026 · Self-released`, the reference's three
 * segments joined with a middot, each dropped when it has nothing to say.
 *
 * **"Self-released" is a DISPLAY fallback for a null label, never a stored value**, which is the whole reason
 * `labelName` is nullable server-side. Both codebases used to hardcode the string with no field behind it, so
 * a band on a label was told it had released the record itself and had no way to correct it (audit G-6). A
 * band that has never set a label still reads "Self-released" — the overwhelmingly common case, and the
 * sentence is true of it — but now that is a default rather than an assertion.
 *
 * A DATELESS release renders no date segment rather than a placeholder, following the reference. It is
 * unreachable from this surface anyway: both {@link pickFeatured} tiers require a date, so a release without
 * one is a Draft and is never featured. The branch exists because this is a pure helper, not because a caller
 * can hit it.
 */
export function releaseMetaLine(
  releaseDate: string | null | undefined,
  trackCount?: number | null,
  labelName?: string | null,
): string {
  const bits: string[] = [];
  // Note the reference counts tracks on a SINGLE too ("1 track"). The EP/Album-only rule is a different
  // surface — the catalog card's type CHIP — and conflating the two drops the segment from the banner.
  if (trackCount && trackCount > 0) bits.push(`${trackCount} track${trackCount === 1 ? "" : "s"}`);
  const date = formatReleaseDate(releaseDate);
  if (date) bits.push(date);
  bits.push(labelName?.trim() || "Self-released");
  return bits.join(" · ");
}

/**
 * The release BUILDER hero's countdown — `T-10 days` · `Out today` · `12 days out` · `Date TBD`, the
 * reference's own four strings verbatim (`ReleasePage.tsx`'s `cd`).
 *
 * Deliberately NOT {@link countdownLabel}, which says "37 days out" / "Out 12 days ago" and is what the hub's
 * count BLOCK reads. The two surfaces phrase the same number differently in the reference — a block with a big
 * numeral above a label, versus one inline token that has to carry its own sign — and collapsing them would
 * silently restyle whichever one lost.
 */
export function releaseCountdownToken(releaseDate: string | null | undefined, today = new Date()): string {
  const n = daysToRelease(releaseDate, today);
  if (n === null) return "Date TBD";
  if (n === 0) return "Out today";
  if (n > 0) return `T-${n} days`;
  return `${-n} days out`;
}

/** Format a domain date as "10/30/2026" — the label release cards' mono date line (ART-335). Blank in → "". */
export function formatNumericDate(date: string | null | undefined): string {
  const d = parseDomainDate(date);
  if (!d) return "";
  return `${d.getMonth() + 1}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
}

/**
 * The builder hero's date — `Fri · Sep 18 · 2026`. The reference builds it by formatting a normal US date and
 * then replacing every comma with a middot, which is why the separators are middots and not commas; done
 * directly here so the string does not depend on a locale's comma placement.
 */
export function formatDottedDate(date: string | null | undefined): string {
  const d = parseDomainDate(date);
  if (!d) return "";
  return `${WEEKDAYS[d.getDay()]} · ${MONTHS[d.getMonth()]} ${d.getDate()} · ${d.getFullYear()}`;
}

/**
 * The builder hero's credit segment — `Whitsett · self-released`.
 *
 * Lowercase `self-released` here against the hub banner's capitalised `Self-released`: the reference spells it
 * both ways, and it is not an inconsistency to tidy — this segment is the tail of a lowercase mono credit line
 * where the hub's is a standalone sentence-cased phrase. Same null-means-unset rule as the hub: the fallback
 * is display only, never stored.
 */
export function releaseCreditLine(bandName: string | null | undefined, labelName: string | null | undefined): string {
  return `${bandName?.trim() || "Artist"} · ${labelName?.trim() || "self-released"}`;
}

/** Format a domain date as "May 8" (no year) — the catalog card's short "Out …" stat. Blank in → "". */
export function formatMonthDay(date: string | null | undefined): string {
  const d = parseDomainDate(date);
  if (!d) return "";
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

// ---- derived lifecycle (TASK-022 D-1(b)) ---------------------------------------------------------------

/**
 * The DISPLAYED lifecycle bucket, derived from the release DATE rather than read straight off the stored
 * `status` column.
 *
 * The reference's central lifecycle rule is *"state is derived, never stored — `isDraft` (no date),
 * `isInCycle` (dated, not yet out), `isReleased` (date passed)"*. We persist a four-value `status`
 * (`draft | in_cycle | released | cancelled`) and nothing on the server reconciles it against `releaseDate`,
 * so an `in_cycle` row dated three months ago renders a pill that LIES. D-1(b): keep the column as the
 * AUTHORING state — `draft` and `cancelled` are decisions a date cannot express — and derive the two dated
 * buckets from the calendar, exactly as the reference does. (D-1(a), a server-side advance when the date
 * passes, is the backend follow-up; this makes the screen honest today without a migration.)
 *
 * Release day itself counts as released (`isReleased = date <= now`), which is why the count block reads
 * "Out today" at zero.
 */
export function displayStatus(
  release: { status?: string | null; releaseDate?: string | null },
  today = new Date(),
): ReleaseStatus {
  if (release.status === "cancelled") return "cancelled";
  if (release.status === "draft") return "draft";
  const n = daysToRelease(release.releaseDate, today);
  if (n === null) return "draft"; // no date at all IS the reference's draft predicate
  return n <= 0 ? "released" : "in_cycle";
}

/** The catalog bucket rank: in cycle → released → drafts (cancelled last; it never reaches the hub). */
const BUCKET_RANK: Record<string, number> = { in_cycle: 0, released: 1, draft: 2, cancelled: 3 };

/**
 * The reference's FIXED catalog order, independent of the order the server returns rows in: in-cycle first
 * by soonest date, then released newest-first, then drafts. Pure + stable (equal keys keep their input
 * order), so the grid, the counts and the tabs can all read one list.
 *
 * Caveat recorded rather than hidden: the list endpoint is a `created_on DESC` keyset, so a client-side sort
 * orders only the LOADED page (the hub fetches 60). Correct at go-live; a catalogue past one page needs the
 * ordering server-side.
 */
export function catalogOrder<T extends { status?: string | null; releaseDate?: string | null }>(
  releases: readonly T[],
  today = new Date(),
): T[] {
  return [...releases]
    .map((r, i) => ({ r, i, bucket: displayStatus(r, today) }))
    .sort((a, b) => {
      const rank = (BUCKET_RANK[a.bucket] ?? 9) - (BUCKET_RANK[b.bucket] ?? 9);
      if (rank !== 0) return rank;
      if (a.bucket === "in_cycle") {
        // soonest first; an in-cycle release always has a date, but sort defensively anyway
        return (a.r.releaseDate ?? "9999-12-31").localeCompare(b.r.releaseDate ?? "9999-12-31");
      }
      if (a.bucket === "released") {
        return (b.r.releaseDate ?? "").localeCompare(a.r.releaseDate ?? ""); // newest first
      }
      return a.i - b.i; // drafts keep server order (created_on DESC)
    })
    .map((x) => x.r);
}

/**
 * The hub catalogue order after ART-201 (Ryan, 2026-09-22): one flat, NEWEST-FIRST list. The lifecycle
 * buckets and their filter pills (All / In cycle / Released / Drafts) were killed in favour of a search box,
 * so the grid no longer sorts by bucket — it is simply "newest at the top".
 *
 * "Newest" is the release DATE, descending. A release with no date yet (a draft still being planned) LEADS —
 * it is the most current work, the same reasoning {@link linkPickerOrder} uses for unscheduled releases — and
 * two undated rows keep server order (`created_on DESC`, so the most recently created is already first).
 *
 * Pure + stable, so a search filter over it stays predictable. Page-scoped like {@link catalogOrder}: the
 * list endpoint is a `created_on DESC` keyset (the hub fetches 60), so this orders the loaded page and a
 * catalogue past one page needs the ordering server-side.
 */
export function catalogNewestFirst<T extends { releaseDate?: string | null }>(releases: readonly T[]): T[] {
  return [...releases]
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      const da = a.r.releaseDate ?? null;
      const db = b.r.releaseDate ?? null;
      if (da === null && db === null) return a.i - b.i; // both undated: server order (created_on DESC)
      if (da === null) return -1; // undated (in-progress) leads
      if (db === null) return 1;
      const cmp = db.localeCompare(da); // ISO dates sort lexically; DESC = newest first
      return cmp !== 0 ? cmp : a.i - b.i;
    })
    .map((x) => x.r);
}

/**
 * The order a release LINK PICKER offers its catalogue in: the furthest-out first, working back toward
 * what already shipped (operator direction, 2026-08-23).
 *
 * Deliberately NOT {@link catalogOrder}. That one is the reference's fixed CATALOGUE order and buckets by
 * lifecycle — drafts last, because a browsing surface leads with what is live. A picker is the opposite
 * question: you are linking a task to work that is still ahead of you, so the rollout you have not finished
 * planning is the likeliest answer and the record that came out last spring is the least likely.
 *
 * **Unscheduled releases lead**, ahead of every dated one. A release with no date is not missing
 * information here — it is the least pinned-down thing in the catalogue, which is exactly the thing that
 * still needs tasks hung off it. Then dates DESCEND: furthest future → most recently out → oldest.
 *
 * Pure and stable (equal keys keep input order), so the listbox and any count read one list.
 */
export function linkPickerOrder<T extends { releaseDate?: string | null; title?: string | null }>(
  releases: readonly T[],
): T[] {
  return [...releases]
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      const aDate = a.r.releaseDate ?? null;
      const bDate = b.r.releaseDate ?? null;
      // Undated first. Not modelled as a sentinel date: "9999-12-31" would sort an undated release BELOW a
      // dated one further out, and there is no date far enough to be safe from a real one.
      if (aDate === null && bDate === null) return a.i - b.i;
      if (aDate === null) return -1;
      if (bDate === null) return 1;
      const byDate = bDate.localeCompare(aDate); // ISO dates compare lexically; descending
      return byDate !== 0 ? byDate : a.i - b.i;
    })
    .map((x) => x.r);
}

/**
 * The featured release — a verbatim port of the reference's `pickFeatured()` (legacy `lib/releases.ts`):
 * **the soonest in-cycle release, and when nothing is in cycle, the most recently RELEASED one**; nothing to
 * feature (drafts only, or an empty catalogue) returns undefined and the banner renders nothing.
 *
 * The second tier is load-bearing, not a nicety. Featuring only in-cycle titles made three of
 * {@link countdownBlock}'s four labels unreachable — a band between rollouts got no banner at all, and
 * "Since release" / "Out today" could never paint, though the reference states both plainly ("the
 * featured count block is contextual: counts down while in cycle, counts up once out").
 * The eyebrow follows the same bucket, so a shipped title never reads "Now in cycle".
 *
 * ("Date TBD" stays out of reach here by design: the count block is fed the FEATURED release, and both tiers
 * require a date — a dateless release is a Draft under {@link displayStatus}. The reference reaches that
 * label from its MOBILE catalog strip, where the featured card follows the selected thumb; that selection
 * behaviour is not ported. The branch stays as the dateless default of a pure helper.)
 */
export function pickFeatured<T extends { status?: string | null; releaseDate?: string | null }>(
  releases: readonly T[],
  today = new Date(),
): T | undefined {
  const inCycle = releases.filter((r) => displayStatus(r, today) === "in_cycle");
  if (inCycle.length > 0) {
    // soonest first; an in-cycle release always has a date, but sort defensively anyway
    return [...inCycle].sort((a, b) =>
      (a.releaseDate ?? "9999-12-31").localeCompare(b.releaseDate ?? "9999-12-31"),
    )[0];
  }
  const out = releases.filter((r) => displayStatus(r, today) === "released");
  if (out.length > 0) {
    return [...out].sort((a, b) => (b.releaseDate ?? "").localeCompare(a.releaseDate ?? ""))[0]; // newest first
  }
  return undefined;
}

/**
 * The featured banner's count block: a big numeral plus ONE of four labels —
 * `Until release` · `Out today` · `Since release` · `Date TBD`. The count carries the "days" meaning, so the
 * label drops the word (Ryan, ART-189: "kill days so it's either 'Until release' or 'Since release'"); the
 * "since" figure has its sign stripped (a positive number under a past-tense label).
 *
 * `value` is null for the two label-only states: there is no day count to show on release day itself, and a
 * dateless release has none to compute.
 */
export function countdownBlock(
  releaseDate: string | null | undefined,
  today = new Date(),
): { value: number | null; label: string } {
  const n = daysToRelease(releaseDate, today);
  if (n === null) return { value: null, label: "Date TBD" };
  if (n === 0) return { value: null, label: "Out today" };
  if (n > 0) return { value: n, label: "Until release" };
  return { value: -n, label: "Since release" };
}

/**
 * The catalog card's short-form stat line: `Draft` · `D-91` (in cycle) · `Out May 8` (released).
 *
 * The reference pairs this with a second `N clicks` slot; we ship ONE slot, because per-release click counts
 * in a LIST context have no source (`ReleaseSummary` carries no clicks and `/smart-link/stats` is per
 * release) — and a column of em-dashes is worse than no column.
 */
export function catalogStat(
  release: { status?: string | null; releaseDate?: string | null },
  today = new Date(),
): string {
  const bucket = displayStatus(release, today);
  if (bucket === "cancelled") return "Cancelled";
  if (bucket === "draft") return "Draft";
  if (bucket === "released") {
    const short = formatMonthDay(release.releaseDate);
    return short ? `Out ${short}` : "Released";
  }
  const n = daysToRelease(release.releaseDate, today);
  return n === null ? "In cycle" : `D-${n}`;
}

/**
 * Created → release progress (0..1) for the overview bar. Before the created date → 0; after release → 1;
 * otherwise the elapsed fraction. Guards a zero/negative span (created == release) → 1. Missing dates → 0.
 */
export function cycleProgress(
  createdOn: string | null | undefined,
  releaseDate: string | null | undefined,
  today = new Date(),
): number {
  const start = createdOn ? new Date(createdOn) : null;
  const end = parseDomainDate(releaseDate);
  if (!start || !end || Number.isNaN(start.getTime())) return 0;
  const span = end.getTime() - start.getTime();
  if (span <= 0) return 1;
  const elapsed = today.getTime() - start.getTime();
  return Math.max(0, Math.min(1, elapsed / span));
}

/** Format integer cents as "$1,234" (whole dollars, no cents) — budget totals / KPI tiles. */
export function formatDollars(cents: number | null | undefined): string {
  const n = typeof cents === "number" ? cents : 0;
  const dollars = Math.round(n / 100);
  const sign = dollars < 0 ? "-" : "";
  return `${sign}$${Math.abs(dollars).toLocaleString("en-US")}`;
}

/** Format integer cents as "$1,234.56" (two decimals) — budget line amounts / Spent/Remaining tiles. */
export function formatMoney(cents: number | null | undefined): string {
  const n = typeof cents === "number" ? cents : 0;
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  const whole = Math.floor(abs / 100).toLocaleString("en-US");
  const frac = (abs % 100).toString().padStart(2, "0");
  return `${sign}$${whole}.${frac}`;
}

/**
 * Sum the per-DSP LIFETIME clicks into the one headline figure the reference's smart-link row and featured
 * banner both show. `null` — never a fabricated 0 — when the stats read errored or has not landed: the leak
 * contract is that an unavailable metric renders "—", while a genuinely loaded 0 is honest and renders "0".
 *
 * Typed structurally so this module stays free of hook/DTO imports (it is pure presentation maths).
 */
export function lifetimeClicks(
  stats: { lifetimeClicksByDsp?: { clicks?: number | null }[] | null } | undefined,
  isError: boolean,
): number | null {
  if (isError || !stats?.lifetimeClicksByDsp) return null;
  return stats.lifetimeClicksByDsp.reduce((sum, c) => sum + (c.clicks ?? 0), 0);
}

/**
 * The mobile smart-link card's CTR (ART-189): LIFETIME clicks ÷ LIFETIME visits, as a whole percent.
 *
 * Deliberately the lifetime ratio, not the server's 30-day {@link `SmartLinkStatsRead.visitToClickPct`} — the
 * card's other two figures are both lifetime, so a 30-day conversion beside them would be a third window in a
 * row that reads as one. It keeps the same leak contract: null (→ "—"), never a fabricated 0, when the read
 * errored (clicks null) or there are no visits to divide by (an undefined ratio). A genuine 0 clicks over real
 * visits is honest and renders "0%".
 */
export function lifetimeCtrPct(
  lifetimeClicksTotal: number | null,
  visitsLifetime: number | null | undefined,
): number | null {
  if (lifetimeClicksTotal === null || !visitsLifetime || visitsLifetime <= 0) return null;
  return Math.round((lifetimeClicksTotal / visitsLifetime) * 100);
}

/**
 * A deterministic dark identity gradient for a release with no cover art yet (renders a gradient
 * placeholder; real cover CDN variants + srcset are /S10). Derives a stable hue from the release id so a
 * release always shows the same gradient — never a fictional cover (the born-blank rule). Returns a CSS
 * `linear-gradient(...)` string using the dark-immersive surface palette (component-scoped dark).
 */
export function releaseGradient(seed: string | null | undefined): string {
  const s = seed ?? "";
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return `linear-gradient(135deg, hsl(${hue} 42% 16%) 0%, hsl(${(hue + 24) % 360} 38% 11%) 60%, hsl(${(hue + 8) % 360} 30% 7%) 130%)`;
}
